import { FilePdfOutlined, PlusOutlined } from '@ant-design/icons';
import {
  billsCount,
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  DOCUMENT_TITLES,
  fullDate,
  isGeneratedMeterNo,
  MEMBER_STATUS_LABELS,
  METER_STATUS_LABELS,
  nextStatuses,
  PHONE_STATUS_LABELS,
  peopleCount,
  present,
} from '@horof/shared';
import { Button } from 'antd';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MemberPanel, MemberStatusDialog, ShartEditor } from './MemberPanel';
import { MeterPanel, MeterStatusDialog } from './MeterPanel';
import { DocumentViewer } from './panel';
import type { MemberDetails, MeterDetails } from './people-data';
import {
  EditableSection,
  KeyValues,
  MEMBER_TONE,
  METER_TONE,
  PHONE_TONE,
  Section,
  Status,
} from './ui';
import { useIsCommittee } from './water-data';

/**
 * A member's «نظرة عامة» (doc 60): read first, «تعديل» per section. The
 * details on the wide side; membership, meters and documents beside them.
 */
export function MemberOverview({
  member,
  onDirtyChange,
}: {
  member: MemberDetails;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canEdit = useIsCommittee();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const active = member.status === 'ACTIVE';
  const meters = present(member.meters.nodes);
  const shart = present(member.shartPeople.nodes);

  return (
    <div className="ui-columns">
      <div className="ui-stack">
        <EditableSection
          title="البيانات"
          canEdit={canEdit && active}
          view={
            <KeyValues
              items={[
                { label: 'الاسم الشخصي', value: member.firstName },
                { label: 'اسم الأب', value: member.fatherName },
                { label: 'الاسم العائلي', value: member.lastName },
                {
                  label: 'الجنس',
                  value: member.sex === 'FEMALE' ? 'أنثى' : 'ذكر',
                },
                { label: 'العنوان', value: member.memberAddress },
                {
                  label: 'العضوية في اللجنة',
                  value: member.committeeRole
                    ? COMMITTEE_ROLE_LABELS[member.committeeRole]
                    : 'ليس عضواً في اللجنة',
                },
                {
                  label: 'هاتف العضو',
                  value: member.phone && (
                    <span className="ltr-num">{member.phone}</span>
                  ),
                },
                {
                  label: 'هاتف القريب',
                  value: member.contactPhone && (
                    <span>
                      <span className="ltr-num">{member.contactPhone}</span>
                      {member.contactName && ` · ${member.contactName}`}
                    </span>
                  ),
                },
                {
                  label: 'البريد الإلكتروني',
                  value: member.email && (
                    <span className="ltr-num">{member.email}</span>
                  ),
                },
                {
                  label: 'طريقة التواصل',
                  value: CHANNEL_LABELS[member.notifyChannel],
                },
                {
                  label: 'رقم الرسائل',
                  value: (
                    <Status tone={PHONE_TONE[member.phoneStatus ?? 'MISSING']}>
                      {PHONE_STATUS_LABELS[member.phoneStatus ?? 'MISSING']}
                    </Status>
                  ),
                },
              ]}
            />
          }
          edit={(done) => (
            <MemberPanel
              id={member.id}
              formOnly
              onDone={done}
              onClose={done}
              onOpen={(id) => navigate(`/members/${id}`)}
              onDirtyChange={onDirtyChange}
            />
          )}
        />
        <Section
          title="الشرط (خدمات الإمام)"
          counter={shart.length}
          description="فرد متزوج من الأسرة لكل سطر. تُطبَّق التغييرات من الفاتورة المقبلة."
        >
          <ShartEditor member={member} canEdit={canEdit && active} bare />
        </Section>
      </div>

      <div className="ui-stack">
        <Section
          title="الانخراط"
          actions={
            canEdit && (
              <Button danger={active} onClick={() => setLeaving(true)}>
                {active ? 'مغادرة الجمعية' : 'إعادة الانخراط'}
              </Button>
            )
          }
        >
          <KeyValues
            columns={2}
            items={[
              {
                label: 'الحالة',
                value: (
                  <Status tone={MEMBER_TONE[member.status]}>
                    {MEMBER_STATUS_LABELS[member.status]}
                  </Status>
                ),
              },
              { label: 'منخرط منذ', value: fullDate(member.joinedAt) },
              !active && {
                label: 'غادر في',
                value: member.leftAt && fullDate(member.leftAt),
              },
              !active && { label: 'السبب', value: member.leftReason },
              {
                label: 'الفواتير غير المؤداة',
                value: member.unpaid.totalCount ? (
                  <Link to="?tab=bills">
                    <Status tone="warning">
                      {billsCount(member.unpaid.totalCount)}
                    </Status>
                  </Link>
                ) : (
                  <Status tone="success">لا شيء</Status>
                ),
              },
              {
                label: 'الشرط',
                value: shart.length ? peopleCount(shart.length) : 'لا أحد',
              },
            ]}
          />
        </Section>

        <Section
          title="العدادات"
          counter={meters.length}
          actions={
            canEdit &&
            active && (
              <Link to={`/meters?panel=new&member=${member.id}`}>
                <Button icon={<PlusOutlined />}>إضافة عداد</Button>
              </Link>
            )
          }
        >
          {meters.length === 0 ? (
            <div className="ui-empty">لا عداد لهذا العضو.</div>
          ) : (
            <ul className="ui-list">
              {meters.map((m) => (
                <li key={m.id}>
                  <Link to={`/meters/${m.id}`}>{m.label}</Link>
                  <Status tone={METER_TONE[m.meterStatus]}>
                    {METER_STATUS_LABELS[m.meterStatus]}
                  </Status>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="الوثائق">
          <ul className="ui-list">
            <li>
              <a href={member.statementLink} target="_blank" rel="noreferrer">
                <FilePdfOutlined /> {DOCUMENT_TITLES.statement}
              </a>
            </li>
            {member.unpaid.totalCount > 0 && (
              <li>
                <a href={member.noticeLink} target="_blank" rel="noreferrer">
                  <FilePdfOutlined /> {DOCUMENT_TITLES.notice}
                </a>
              </li>
            )}
          </ul>
        </Section>
      </div>
      {leaving && (
        <MemberStatusDialog member={member} onClose={() => setLeaving(false)} />
      )}
    </div>
  );
}

/** A meter's «نظرة عامة» (doc 60): the meter, its status, its photo. */
export function MeterOverview({
  meter,
  onDirtyChange,
}: {
  meter: MeterDetails;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canEdit = useIsCommittee();
  const navigate = useNavigate();
  const [changing, setChanging] = useState(false);
  const connected = meter.meterStatus === 'CONNECTED';

  return (
    <div className="ui-columns">
      <div className="ui-stack">
        <EditableSection
          title="العداد"
          canEdit={canEdit}
          description="الرقم والعنوان والعضو، وصورة العداد."
          view={
            <KeyValues
              columns={2}
              items={[
                {
                  label: 'رقم العداد',
                  value: isGeneratedMeterNo(meter.meterNo) ? (
                    <span className="ui-empty">بدون رقم</span>
                  ) : (
                    <span className="ltr-num">{meter.meterNo}</span>
                  ),
                },
                {
                  label: 'العنوان أو الاسم المعروف به',
                  value: meter.meterAddress,
                },
                {
                  label: 'العضو',
                  value: meter.member && (
                    <Link to={`/members/${meter.member.id}`}>
                      {meter.member.fullName}
                    </Link>
                  ),
                },
                { label: 'مربوط منذ', value: fullDate(meter.connectedAt) },
              ]}
            />
          }
          edit={(done) => (
            <MeterPanel
              id={meter.id}
              formOnly
              onDone={done}
              onClose={done}
              onOpen={(id) => navigate(`/meters/${id}`)}
              onDirtyChange={onDirtyChange}
            />
          )}
        />
        <Section title="صورة العداد">
          {meter.photo ? (
            <DocumentViewer photo={meter.photo} title="صورة العداد" />
          ) : (
            <div className="ui-empty">
              لا صورة لهذا العداد. تُضاف من «تعديل».
            </div>
          )}
        </Section>
      </div>
      <div className="ui-stack">
        <Section
          title="الحالة"
          actions={
            canEdit &&
            nextStatuses(meter.meterStatus).length > 0 && (
              <Button onClick={() => setChanging(true)}>تغيير الحالة</Button>
            )
          }
        >
          <KeyValues
            columns={2}
            items={[
              {
                label: 'الحالة',
                value: (
                  <Status tone={METER_TONE[meter.meterStatus]}>
                    {METER_STATUS_LABELS[meter.meterStatus]}
                  </Status>
                ),
              },
              {
                label: 'منذ',
                value: meter.statusChangedAt && fullDate(meter.statusChangedAt),
              },
              { label: 'السبب', value: meter.statusReason, wide: true },
            ]}
          />
          {!connected && (
            <div className="ui-section-description">
              غير المربوط لا يظهر في الجولة ولا تصدر له فواتير.
            </div>
          )}
        </Section>
      </div>
      {changing && (
        <MeterStatusDialog meter={meter} onClose={() => setChanging(false)} />
      )}
    </div>
  );
}
